/**
 * header.js — site chrome behavior.
 * Ports of:
 *  - components/layout/announcement-bar.tsx   (hide on scroll down past 100px,
 *    show on scroll up / near top)
 *  - components/layout/scroll-header.tsx      (transparent-over-hero header on
 *    the landing page, solid elsewhere / on mobile, hide-on-scroll-down; the
 *    dynamic class strings below are verbatim copies of the React template
 *    literals so the compiled CSS is identical)
 *  - components/layout/navbar/mobile-menu.tsx (close the MobileMenu drawer
 *    when resizing past the xl breakpoint)
 *  - components/newsletter-popup.tsx          (delayed popup gated by the
 *    newsletter_subscribed / newsletter_dismissed cookies; shows the native
 *    {% form 'customer' %} signup)
 *
 * Also coordinates the announcement bar with the header: while the bar is
 * visible the header is offset below it, and --announcement-offset feeds the
 * non-landing content padding set in sections/header.liquid.
 *
 * Loaded with `defer` from several sections; the guard runs it once.
 */
(function () {
  "use strict";
  if (window.__munchiesChromeInit) return;
  window.__munchiesChromeInit = true;

  var XL_BREAKPOINT = 1280;

  /* ------------------------------------------------------------------ */
  /* Cookie helpers (js-cookie port, same flags as the headless app)     */
  /* ------------------------------------------------------------------ */
  function getCookie(name) {
    var match = document.cookie.split("; ").find(function (row) {
      return row.indexOf(name + "=") === 0;
    });
    return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
  }

  function setCookie(name, value, days) {
    var str =
      name +
      "=" +
      encodeURIComponent(value) +
      "; path=/; samesite=lax; expires=" +
      new Date(Date.now() + days * 864e5).toUTCString();
    if (location.protocol === "https:") str += "; secure";
    document.cookie = str;
  }

  /* ------------------------------------------------------------------ */
  /* Announcement bar + scroll header                                    */
  /* ------------------------------------------------------------------ */
  // Captured (re)freshably: a header/announcement re-render in the editor
  // swaps these nodes, so the persistent window scroll/resize handlers below
  // must be repointed at the current elements via captureChromeEls().
  var bar, header, isLandingPage;
  function captureChromeEls() {
    bar = document.querySelector("[data-announcement-bar]");
    header = document.querySelector("[data-scroll-header]");
    isLandingPage = document.body.hasAttribute("data-landing-page");
  }
  captureChromeEls();

  var lastScrollY = 0;
  var isMobile = window.innerWidth < XL_BREAKPOINT;
  var isScrolled = false;
  var isHeroScrolledThrough = false;
  var isHeaderHidden = false;
  var barVisible = true;

  function setAnnouncementOffset() {
    document.documentElement.style.setProperty(
      "--announcement-offset",
      (bar ? bar.offsetHeight : 0) + "px",
    );
  }

  function applyChromeState() {
    if (bar) {
      bar.style.transform = barVisible ? "translateY(0)" : "translateY(-100%)";
    }
    if (!header) return;

    // Calculate opacity and background based on scroll position
    // (verbatim port of the scroll-header.tsx derivations)
    var bgOpacity = isMobile
      ? 1
      : isLandingPage
        ? isHeroScrolledThrough
          ? 1
          : 0
        : 1;
    var backdropBlur = bgOpacity * 12;
    var textColor = isMobile
      ? "text-primary"
      : isLandingPage
        ? isHeroScrolledThrough
          ? "text-tertiary"
          : "text-white"
        : "text-tertiary";
    var hoverColor = isMobile
      ? "hover:text-primary"
      : isLandingPage
        ? isHeroScrolledThrough
          ? "hover:text-primary"
          : "hover:text-white/80"
        : "hover:text-primary";
    // Desktop-only elements (logo crossfade, account icon, language switcher)
    var transparentChrome = isLandingPage && !isHeroScrolledThrough;
    // CartButton: usePrimary = isMobile || isScrolled (scrollProgress > 0)
    var usePrimaryCart =
      isMobile || (isLandingPage ? isHeroScrolledThrough : true);

    // Follows the rebrandable --quaternary token (was a hardcoded rgba(245,245,240)).
    header.style.backgroundColor =
      "color-mix(in srgb, var(--quaternary) " + bgOpacity * 100 + "%, transparent)";
    header.style.backdropFilter = "blur(" + backdropBlur + "px)";
    header.style.setProperty(
      "-webkit-backdrop-filter",
      "blur(" + backdropBlur + "px)",
    );
    header.style.top = bar && barVisible ? bar.offsetHeight + "px" : "0px";

    var solidShadow = isLandingPage ? isHeroScrolledThrough : isScrolled;
    header.classList.toggle("shadow-lg", solidShadow);
    header.classList.toggle("shadow-none", !solidShadow);
    header.classList.toggle("-translate-y-full", isHeaderHidden);
    header.classList.toggle("translate-y-0", !isHeaderHidden);

    header.querySelectorAll("[data-header-nav-link]").forEach(function (link) {
      var active = link.getAttribute("data-active") === "true";
      link.className =
        "font-heading text-lg uppercase tracking-[0.05em] transition-colors duration-300 " +
        textColor +
        " " +
        hoverColor +
        (active ? " text-primary" : "");
    });

    var logoWhite = header.querySelector("[data-header-logo-white]");
    if (logoWhite) {
      logoWhite.className =
        "h-auto w-auto max-h-12 object-contain absolute inset-0 transition-opacity duration-300 " +
        (transparentChrome ? "opacity-100" : "opacity-0");
    }
    var logoRegular = header.querySelector("[data-header-logo-regular]");
    if (logoRegular) {
      logoRegular.className =
        "h-auto w-auto max-h-12 object-contain transition-opacity duration-300 " +
        (transparentChrome ? "opacity-0" : "opacity-100");
    }

    var account = header.querySelector("[data-account-link]");
    if (account) {
      account.className =
        "group relative hidden xl:flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 " +
        (transparentChrome
          ? "bg-white/10 text-white hover:bg-white/20"
          : "bg-primary/10 text-primary hover:bg-primary hover:text-white");
    }

    var cartBtn = header.querySelector("[data-cart-button]");
    if (cartBtn) {
      cartBtn.className =
        "group relative flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 cursor-pointer " +
        (usePrimaryCart
          ? "bg-primary/10 text-primary hover:bg-primary hover:text-white"
          : "bg-white/10 text-white hover:bg-white/20");
      var badge = cartBtn.querySelector("[data-cart-count]");
      if (badge) {
        // className only — textContent + [hidden] stay owned by cart.js
        badge.className =
          "absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-body-bold " +
          (usePrimaryCart ? "bg-primary text-white" : "bg-white text-primary");
      }
    }

    header.querySelectorAll("[data-lang-other]").forEach(function (btn) {
      btn.className =
        "mr-2 h-10 w-10 shrink-0 rounded-full font-body-semibold text-sm transition-all duration-300 cursor-pointer flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 " +
        (transparentChrome
          ? "bg-white/10 text-white hover:bg-white/20"
          : "bg-primary/10 text-primary hover:bg-primary hover:text-white");
    });
    var langCurrent = header.querySelector("[data-lang-current]");
    if (langCurrent) {
      langCurrent.className =
        "h-10 w-10 rounded-full font-body-semibold text-sm transition-all duration-300 flex items-center justify-center " +
        (transparentChrome ? "bg-white text-primary" : "bg-primary text-white");
    }
  }

  var ticking = false;
  function handleScroll() {
    var currentScrollY = window.scrollY;

    // Throttle updates using requestAnimationFrame
    if (!ticking) {
      window.requestAnimationFrame(function () {
        var viewportHeight = window.innerHeight;

        // Announcement bar: hide when scrolling down, show when scrolling up
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          barVisible = false;
        } else if (currentScrollY < lastScrollY) {
          barVisible = true;
        }
        // Always show announcement bar at the very top
        if (currentScrollY < 10) {
          barVisible = true;
        }

        // Header becomes solid and logo appears only after the hero
        // (min-h-fixed-screen = 100vh) is fully scrolled through
        isHeroScrolledThrough = currentScrollY >= viewportHeight;
        isScrolled = isLandingPage ? isHeroScrolledThrough : currentScrollY > 20;

        // Hide header on scroll down, show on scroll up
        var scrollDelta = currentScrollY - lastScrollY;
        var menuOpen =
          document.body.getAttribute("data-drawer-active") === "MobileMenu";
        if (scrollDelta > 10 && currentScrollY > 100 && !menuOpen) {
          isHeaderHidden = true;
        } else if (scrollDelta < -5) {
          isHeaderHidden = false;
        }

        lastScrollY = currentScrollY;
        applyChromeState();
        ticking = false;
      });
      ticking = true;
    }
  }

  window.addEventListener("scroll", handleScroll, { passive: true });

  function handleResize() {
    isMobile = window.innerWidth < XL_BREAKPOINT;
    // mobile-menu.tsx: close the menu when leaving mobile widths
    if (window.innerWidth > XL_BREAKPOINT && window.themeUtils) {
      window.themeUtils.closeDrawer("MobileMenu");
    }
    setAnnouncementOffset();
    applyChromeState();
  }
  window.addEventListener("resize", handleResize);

  // Hamburger aria state (port of the React aria-expanded / aria-label swap).
  // Handlers re-query the button at call time so they stay correct after a
  // header re-render; bound once per MobileMenu node via __ariaBound.
  function bindMobileMenuAria() {
    var mobileMenu = document.getElementById("MobileMenu");
    if (!mobileMenu || mobileMenu.__ariaBound) return;
    mobileMenu.__ariaBound = true;
    mobileMenu.addEventListener("drawer:open", function () {
      var menuButton = document.querySelector("[data-mobile-menu-button]");
      if (!menuButton) return;
      menuButton.setAttribute("aria-expanded", "true");
      if (menuButton.dataset.closeLabel) {
        menuButton.setAttribute("aria-label", menuButton.dataset.closeLabel);
      }
    });
    mobileMenu.addEventListener("drawer:close", function () {
      var menuButton = document.querySelector("[data-mobile-menu-button]");
      if (!menuButton) return;
      menuButton.setAttribute("aria-expanded", "false");
      if (menuButton.dataset.openLabel) {
        menuButton.setAttribute("aria-label", menuButton.dataset.openLabel);
      }
    });
  }
  bindMobileMenuAria();

  setAnnouncementOffset();
  applyChromeState();
  handleScroll(); // Initial check

  /* ------------------------------------------------------------------ */
  /* Newsletter signup popup state                                       */
  /* ------------------------------------------------------------------ */
  var popupSubscribed = false;

  /* ------------------------------------------------------------------ */
  /* Newsletter popup — port of newsletter-popup.tsx                     */
  /* ------------------------------------------------------------------ */
  var newsletterPopup = document.querySelector("[data-newsletter-popup]");

  // A successful newsletter signup reloads the page with ?customer_posted=true
  // (both the footer form and this popup are {% form 'customer' %}). Open the
  // popup so the subscriber gets the centered "thank you" confirmation — its
  // form renders the success state — then auto-close after 10s and mark them
  // subscribed so it won't re-open. This also avoids dropping the user at the
  // #contact_form footer anchor (which lands awkwardly with content-visibility
  // sections above), which read as a "cut off" footer.
  if (
    newsletterPopup &&
    /[?&]customer_posted=true/.test(window.location.search)
  ) {
    popupSubscribed = true;
    setCookie("newsletter_subscribed", "true", 365);
    if (window.themeUtils) {
      window.themeUtils.openModal(newsletterPopup.id);
      setTimeout(function () {
        if (window.themeUtils) window.themeUtils.closeModal(newsletterPopup.id);
      }, 10000);
    }
  }

  // Theme editor: no timed auto-open and no cookie writes. The popup is
  // previewed by selecting its section in the sidebar (see the design-mode
  // script in sections/newsletter-popup.liquid); writing the dismissed cookie
  // there would silently block the merchant's own preview for 7 days.
  var popupDesignMode = window.Shopify && window.Shopify.designMode;

  if (newsletterPopup && !popupDesignMode) {
    var delay = parseInt(newsletterPopup.getAttribute("data-delay"), 10);
    if (isNaN(delay) || delay <= 0) delay = 30000; // Show after 30 seconds
    // Skip scheduling entirely if already subscribed (e.g. just signed up) or
    // previously dismissed, so the timed popup never fights the success dialog.
    if (
      !getCookie("newsletter_subscribed") &&
      !getCookie("newsletter_dismissed")
    ) {
      setTimeout(function () {
        if (
          !getCookie("newsletter_subscribed") &&
          !getCookie("newsletter_dismissed")
        ) {
          if (window.themeUtils) window.themeUtils.openModal(newsletterPopup.id);
        }
      }, delay);
    }
    newsletterPopup.addEventListener("modal:close", function () {
      // Closed without subscribing -> set dismissed cookie for 7 days
      if (!popupSubscribed) setCookie("newsletter_dismissed", "true", 7);
    });
  }

  /* Theme editor: re-point the persistent scroll/resize chrome handlers at the
     freshly re-rendered header/announcement-bar and re-apply state. Called by
     theme.js on shopify:section:load (editor-only). The window listeners stay
     bound once and read the reassigned `bar`/`header` closure vars; newsletter
     scheduling is intentionally NOT re-run. Idempotent. */
  window.themeHeader = {
    reinit: function () {
      captureChromeEls();
      bindMobileMenuAria();
      setAnnouncementOffset();
      applyChromeState();
    },
  };
})();
